import type {
	GridColumnProps,
	GridColumnReorderEvent,
	GridColumnResizeEvent,
	GridPageChangeEvent,
} from "@tql/ui-components-react/kendo/grid";

import { ColumnChooser } from "~/shared/components/column-chooser/column-chooser.tsx";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { Invoice } from "~/shared/schemas/invoice-schema";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import { DEFAULT_EXTERNAL_GRID_PAGER_SETTINGS } from "~/shared/utils/default-grid-pager-settings.ts";

import type { PaymentGridMobileProps } from "./mobile/payment-grid-mobile/payment-grid-mobile.tsx";
import { PaymentGridMobile } from "./mobile/payment-grid-mobile/payment-grid-mobile.tsx";

// Everything besides invoices/isLoading here (selection + document-click) is
// only ever consumed by PaymentGridMobile, so its own prop type is the single
// source of truth for that contract instead of duplicating the field list.
type PaymentGridProps = Omit<
	PaymentGridMobileProps,
	"invoices" | "isLoading"
> & {
	invoices: Invoice[];
	isLoading: boolean;
	allColumns: BoardColumn[];
	visibleColumns: BoardColumn[];
	gridKey: number;
	arePreferencesReady: boolean;
	onColumnVisibilityChange: (column: BoardColumn) => void;
	onColumnReorder: (event: GridColumnReorderEvent) => void;
	onColumnResize: (event: GridColumnResizeEvent) => void;
	resetColumns: () => void;
	onPageChange?: (event: GridPageChangeEvent) => void;
	skip?: number;
	take?: number;
};

export function PaymentGrid({
	invoices,
	isLoading,
	allColumns,
	visibleColumns,
	gridKey,
	arePreferencesReady,
	onColumnVisibilityChange,
	onColumnReorder,
	onColumnResize,
	resetColumns,
	onPageChange,
	skip,
	take,
	...mobileProps
}: Readonly<PaymentGridProps>) {
	const isMobile = useIsMobile();

	if (isMobile) {
		return (
			<PaymentGridMobile
				invoices={invoices}
				isLoading={isLoading}
				{...mobileProps}
			/>
		);
	}

	return (
		<>
			<div className={"payment-column-chooser"}>
				<ColumnChooser
					secondary
					columns={allColumns}
					onColumnToggle={onColumnVisibilityChange}
					onResetColumns={resetColumns}
				/>
			</div>

			<div className={"invoice-grid"} data-testid={"invoice-grid"}>
				<TraxGrid
					// Remount once the persisted preferences have loaded so Kendo
					// reads the saved column widths/order on a fresh mount instead
					// of locking in the default widths from the initial render.
					key={`payment-grid-${gridKey}-${
						arePreferencesReady ? "ready" : "pending"
					}`}
					columns={visibleColumns as GridColumnProps[]}
					data={invoices}
					editable={false}
					filterable={true}
					loading={isLoading}
					loadingRows={10}
					onColumnReorder={onColumnReorder}
					onColumnResize={onColumnResize}
					onPageChange={onPageChange}
					pageable={DEFAULT_EXTERNAL_GRID_PAGER_SETTINGS}
					reorderable={true}
					resizable={true}
					skip={skip}
					style={{ height: "100%" }}
					take={take}
				/>
			</div>
		</>
	);
}
